import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import type { CatalogSkuCreateInput } from "@/modules/catalog/domain/contracts";
import { FieldHelpHint } from "@/shared/ui/FieldHelpHint";

const NONE_VALUE = "__none__"; // Radix Select ບໍ່ອະນຸຍາດ value=""
const CONDITION_OPTIONS = [
  { value: NONE_VALUE, label: "— ບໍ່ເລືອກ —" },
  { value: "new", label: "ໃໝ່" },
  { value: "used", label: "ໃຊ້ແລ້ວ" },
  { value: "refurb", label: " Refurb" },
];

const SkuFormSchema = z.object({
  oemPartNo: z.string().max(100).optional().or(z.literal("")),
  condition: z.enum(["new", "used", "refurb", NONE_VALUE]).optional(),
});

type SkuFormValues = z.infer<typeof SkuFormSchema>;

type SkuFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingSku: {
    id: string;
    oemPartNo: string | null;
    condition: string | null;
  } | null;
  onSubmit: (
    values: Omit<CatalogSkuCreateInput, "catalogProductId">,
  ) => void | Promise<void>;
};

export function SkuFormModal({
  open,
  onOpenChange,
  editingSku,
  onSubmit,
}: SkuFormModalProps) {
  const methods = RHF.useForm<SkuFormValues>({
    resolver: zodResolver(SkuFormSchema),
    defaultValues: {
      oemPartNo: "",
      condition: NONE_VALUE,
    },
    values: open
      ? {
          oemPartNo: editingSku?.oemPartNo ?? "",
          condition:
            editingSku?.condition === "new" ||
            editingSku?.condition === "used" ||
            editingSku?.condition === "refurb"
              ? editingSku.condition
              : NONE_VALUE,
        }
      : undefined,
  });

  const handleSubmit = (vals: SkuFormValues) => {
    onSubmit({
      oemPartNo: vals.oemPartNo?.trim() || null,
      condition:
        vals.condition !== NONE_VALUE && vals.condition != null ?
          vals.condition
        : null,
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={editingSku ? "ແກ້ໄຂ SKU" : "ເພີ່ມ SKU ຫຼັກ"}
      description="OEM Part No ແລະ ສະພາບ (condition) — ບໍ່ມີລາຄາ/barcode ຢູ່ທີ່ນີ້ (ເປັນຕໍ່ຮ້ານໃນ C2)"
      size="md"
    >
      <FormRoot<SkuFormValues>
        methods={methods}
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <FormInput
          name="oemPartNo"
          label={
            <FieldHelpHint label="OEM Part No">
              ລະຫັດຊິ້ນສ່ວນຈາກໂຮງງານ (OEM) ຂອງ SKU ຫຼັກໃນ catalog —
              ບໍ່ແມ່ນລະຫັດຂາຍ / Shop SKU ຂອງຮ້ານ.
            </FieldHelpHint>
          }
          placeholder="ບໍ່ບັງຄັບ"
        />
        <FormSelect
          name="condition"
          label="ສະພາບ"
          options={CONDITION_OPTIONS}
        />
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button type="submit">ບັນທຶກ</Button>
        </div>
      </FormRoot>
    </Modal>
  );
}
